import { Stack } from 'component-library';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BenchmarkType, IndicatorName, PlotReportFactLabelNameEnum } from '@/api/rest/resources/types/fact';
import { CarbonStorageBGScenarioChart } from '@/components/Charts/CarbonStorageBGScenario';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { getChartDataWithBenchmarks, kgToTonneFormatter } from '@/components/Charts/utils';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

import { useIsLandPortionProject } from '../../hooks/useIsLandPortionProject';

export const CarbonGraphTile = () => {
  const isLandPortionProject = useIsLandPortionProject();
  return isLandPortionProject ? <ProjectCarbonGraph /> : <PlotCarbonGraph />;
};

export const ProjectCarbonGraph = () => {
  const { ...filters } = useWatch();
  const { analysisType } = useNCCardContext();

  const carbonBgHistoricFact =
    analysisType === 'total'
      ? IndicatorName.soil_carbon_historic_total_graph
      : IndicatorName.soil_carbon_historic_per_ha_graph;
  const { [carbonBgHistoricFact]: carbonBgHistoric } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(carbonBgHistoricFact);
  const cbgHistoricData = carbonBgHistoric?.value ?? [];

  const carbonBgUpliftPotentialFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_uplift_potential_total_graph
      : IndicatorName.permanent_soil_carbon_uplift_potential_per_ha_graph;
  const { [carbonBgUpliftPotentialFact]: carbonBgUpliftPotential } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    carbonBgUpliftPotentialFact,
    null,
    filters,
  );
  const cbgPotentialData = carbonBgUpliftPotential?.value;

  const carbonBgBenchmarkFact =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_total_benchmark
      : IndicatorName.permanent_soil_carbon_per_ha_benchmark;
  const { [carbonBgBenchmarkFact]: carbonBgLowBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBgBenchmarkFact,
    null,
    { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low },
  );

  const { [carbonBgBenchmarkFact]: carbonBgModerateBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBgBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
    },
  );

  const { [carbonBgBenchmarkFact]: carbonHighBenchmark } = useOrganizationIndicatorReportingFact<number>(
    carbonBgBenchmarkFact,
    null,
    {
      [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
    },
  );

  const benchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: carbonHighBenchmark,
    moderateBenchmarkFact: carbonBgModerateBenchmark,
    lowBenchmarkFact: carbonBgLowBenchmark,
    formatter: kgToTonneFormatter,
  });

  /**
   * We only null check for cbgPotentialData since cbgPotentialData is the primary source data of the graph
   * reference lines are displayed only when cbgPotentialData is rendered
   */
  const noChartData = !cbgPotentialData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...cbgHistoricData,
    ...cbgPotentialData,
    ...getChartDataWithBenchmarks({
      chartData: cbgPotentialData,
      benchmarks: { low: carbonBgLowBenchmark, moderate: carbonBgModerateBenchmark, high: carbonHighBenchmark },
    }),
  ];

  return <CarbonGraph chartData={chartData} benchmarks={benchmarks} />;
};

export const PlotCarbonGraph = () => {
  const { analysisType } = useNCCardContext();
  const { landPortionId: plotId, ...filters } = useWatch();

  const carbonBgHistoricFactType =
    analysisType === 'total'
      ? IndicatorName.soil_carbon_historic_total_graph
      : IndicatorName.soil_carbon_historic_per_ha_graph;

  const carbonBgUpliftPotentialFactType =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_uplift_potential_total_graph
      : IndicatorName.permanent_soil_carbon_uplift_potential_per_ha_graph;

  const carbonBenchmarkFactType =
    analysisType === 'total'
      ? IndicatorName.permanent_soil_carbon_total_benchmark
      : IndicatorName.permanent_soil_carbon_per_ha_benchmark;

  const facts = usePlotFacts<GraphFact[]>([carbonBgHistoricFactType, carbonBenchmarkFactType], plotId);
  const filteredFacts = usePlotFacts<GraphFact[]>([carbonBgUpliftPotentialFactType], plotId, filters);

  const carbonBgHistoric = getFactFromFactArray<GraphFact[]>(facts, carbonBgHistoricFactType);
  const cbgHistoricData = carbonBgHistoric?.value ?? [];

  const carbonBgUpliftPotential = getFactFromFactArray<GraphFact[]>(filteredFacts, carbonBgUpliftPotentialFactType);
  const cbgPotentialData = carbonBgUpliftPotential?.value;

  const carbonBgLowBenchmark = getFactFromFactArray<GraphFact[]>(facts, carbonBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low,
  });
  const cbgLowBenchmarkData = (carbonBgLowBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });
  const carbonBgModerateBenchmark = getFactFromFactArray<GraphFact[]>(facts, carbonBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate,
  });
  const cbgModerateBenchmarkData = (carbonBgModerateBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });
  const carbonHighBenchmark = getFactFromFactArray<GraphFact[]>(facts, carbonBenchmarkFactType, {
    [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high,
  });
  const cbgHighBenchmarkData = (carbonHighBenchmark?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  const benchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: carbonHighBenchmark,
    moderateBenchmarkFact: carbonBgModerateBenchmark,
    lowBenchmarkFact: carbonBgLowBenchmark,
    formatter: kgToTonneFormatter,
  });

  /**
   * We only null check for cbgPotentialData since cbgPotentialData is the primary source data of the graph
   * reference lines are displayed only when cbgPotentialData is rendered
   */
  const noChartData = !cbgPotentialData?.length;
  if (noChartData) {
    return <NoData />;
  }

  const chartData = [
    ...cbgHistoricData,
    ...cbgPotentialData,
    ...cbgLowBenchmarkData,
    ...cbgModerateBenchmarkData,
    ...cbgHighBenchmarkData,
  ];

  return <CarbonGraph chartData={chartData} benchmarks={benchmarks} />;
};

export const CarbonGraph = ({
  chartData,
  benchmarks,
}: {
  chartData: GraphFact[];
  benchmarks: (number | undefined)[];
}) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();
  const timeFrame = useWatch({ name: 'timeFrame' });

  const { zoom, setZoom } = useChartZoom();

  return (
    <Stack center data-testid='carbon-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-6 lg:pl-1'>
        <ChartHeading
          heading={t('shared.projects.project.impactProjections.carbon.labels.graphHeadline', { timeFrame })}
          popover={{
            title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
            body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={zoom}
          setZoom={setZoom}
        />
        <CarbonStorageBGScenarioChart
          analysisType={analysisType}
          data={chartData}
          benchmarks={benchmarks}
          timeFrame={Number(timeFrame)}
          showTooltip={true}
          className='relative'
          data-testid='carbon-storage-bg-potential-chart'
          zoom={zoom}
          setZoom={setZoom}
        />
      </Stack>
    </Stack>
  );
};
